import { type Property, type Tenancy, getSupabase } from "@/lib/supabase";
import { useEffect, useState } from "react";
import { useAuth } from "./useAuth";

export type TenancyWithProperty = {
  tenancy: Tenancy;
  property: Property;
};

type State = {
  loading: boolean;
  error: string | null;
  tenancies: TenancyWithProperty[];
};

/**
 * Fetch every active or upcoming tenancy for the current user, with the
 * matching property eagerly attached.
 *
 * RLS already restricts results to `tenant_user_id = auth.uid()`. We add an
 * explicit `.eq` filter for clarity and so the query plan stays cheap.
 */
export function useTenancy() {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<State>({
    loading: true,
    error: null,
    tenancies: [],
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setState({ loading: false, error: null, tenancies: [] });
      return;
    }

    let cancelled = false;
    setState({ loading: true, error: null, tenancies: [] });

    (async () => {
      const supabase = getSupabase();
      const { data: tenancies, error: tErr } = (await supabase
        .from("tenancies")
        .select("*")
        .eq("tenant_user_id", user.id)
        .in("status", ["upcoming", "active"])
        .order("start_date", { ascending: false })) as {
        data: Tenancy[] | null;
        error: { message: string } | null;
      };

      if (cancelled) return;
      if (tErr) {
        setState({ loading: false, error: tErr.message, tenancies: [] });
        return;
      }
      const list = tenancies ?? [];
      if (list.length === 0) {
        setState({ loading: false, error: null, tenancies: [] });
        return;
      }
      const propertyIds = Array.from(new Set(list.map((t) => t.property_id)));
      const { data: properties, error: pErr } = (await supabase
        .from("properties")
        .select("*")
        .in("id", propertyIds)) as {
        data: Property[] | null;
        error: { message: string } | null;
      };
      if (cancelled) return;
      if (pErr) {
        setState({ loading: false, error: pErr.message, tenancies: [] });
        return;
      }
      const propertyMap = new Map((properties ?? []).map((p) => [p.id, p]));
      const joined: TenancyWithProperty[] = list
        .map((t) => {
          const p = propertyMap.get(t.property_id);
          return p ? { tenancy: t, property: p } : null;
        })
        .filter((x): x is TenancyWithProperty => x !== null);
      setState({ loading: false, error: null, tenancies: joined });
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return state;
}
